// httpindexloader.ts ↔ mpshared/indexloader.cpp (IndexLoader — the streamed
// JSON catalog parser) + game/httpindexloader.cpp (HttpIndexLoader — the HTTP
// fetch + the Add-On form's sorting/merging, gphttp + gppackm consumers).
//
// The catalog wire shape (confirmed against the live content worker,
// cloudflare/workers/content — GET /wi/index): a JSON array of 6-element
// arrays [id, "md5hex32", "title", cPlayersMin, cPlayersMax, cMissions].
//
// SHAPE NOTES:
//  1. The yajl streaming wrapper (json::JsonBuilder + ArrayItemsCallback,
//     yajl/wrapper/jsonbuilder.cpp) becomes JsonArrayItemsBuilder below — the
//     same contract: Update(chunk) feeds bytes-worth of text incrementally,
//     each COMPLETED top-level array item fires OnObject (already parsed),
//     malformed input fails Update. The C++ feeds raw UTF-8 bytes to yajl;
//     here a streaming TextDecoder feeds the same bytes as text.
//  2. CreateMissionList reaches the C++ globals (gpakr/gppackm/gppim); the TS
//     twin (ui/missionpick.ts) takes a deps bundle, so HttpIndexLoader takes
//     an injected createMissionList(ppackid, mlt) seam — dlc-forms.ts wires
//     it to the real CreateMissionList over the form env's MissionListDeps.
//  3. IndexLoader::InitFromFile (the fopen/fread chunk loop — only the
//     server's index watcher uses it on a file) ports as InitFromText over
//     the same builder.

import type { PackId } from '../io/savegame.ts';
import { FromHex, ToInteger as _ToInteger, HeadersMap, HttpRequest } from './httpservice.ts';
import {
  kszIndexUrl,
  kdwClientID,
  knVersionSimulation,
  type HttpService,
  type HttpResponseHandler,
  type ProgressCallback,
} from './httpservice.ts';
import type { PackManager } from './httppackmanager.ts';

// game/missionlist.h:24 — kmltAll (what AddIndexEntry enumerates)
export const kmltAll = 0;

// mpshared/indexloader.h:10-17
export interface IndexEntry {
  packid: PackId;
  title: string;
  cPlayersMin: number;
  cPlayersMax: number;
  cMissions: number;
  inIndex: boolean;
}

/** The CreateMissionList seam (SHAPE NOTE 2) — the slice of
 *  ui/missionpick.ts MissionList that AddIndexEntry consumes. */
export interface MissionListLike {
  GetCount(): number;
  GetMissionDescription(
    i: number,
  ): { szPackName: string; cPlayersMin: number; cPlayersMax: number } | null;
}
export type CreateMissionListFn = (ppackid: PackId, mlt: number) => MissionListLike | null;

//
// JsonArrayItemsBuilder — yajl/wrapper/jsonbuilder.cpp's streaming
// array-items mode (SHAPE NOTE 1)
//

export interface ArrayItemsCallback {
  OnObject(obj: unknown): void;
}

export class JsonArrayItemsBuilder {
  private cb: ArrayItemsCallback | null = null;
  private started = false; // consumed the top-level '['
  private done = false; // consumed the top-level ']'
  private depth = 0; // nesting depth INSIDE the current item
  private inString = false;
  private escaped = false;
  private item = ''; // the current item's accumulated text
  private inItem = false;
  private failed = false;

  Start(pcb: ArrayItemsCallback | null): void {
    this.Reset();
    this.cb = pcb;
  }

  Reset(): void {
    this.cb = null;
    this.started = false;
    this.done = false;
    this.depth = 0;
    this.inString = false;
    this.escaped = false;
    this.item = '';
    this.inItem = false;
    this.failed = false;
  }

  /** Feed a text chunk; false = parse error (yajl_parse failure). */
  Update(text: string): boolean {
    if (this.failed) return false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];

      if (this.inItem) {
        if (this.inString) {
          this.item += ch;
          if (this.escaped) this.escaped = false;
          else if (ch === '\\') this.escaped = true;
          else if (ch === '"') this.inString = false;
          continue;
        }
        if (ch === '"') {
          this.inString = true;
          this.item += ch;
          continue;
        }
        if (ch === '{' || ch === '[') {
          this.depth++;
          this.item += ch;
          continue;
        }
        if (ch === '}' || ch === ']') {
          if (this.depth === 0) {
            // a scalar item ended at the top-level ']'
            if (ch === '}') return this.fail();
            if (!this.emit()) return false;
            this.done = true;
            continue;
          }
          this.depth--;
          this.item += ch;
          if (this.depth === 0) {
            // a composite item completed
            if (!this.emit()) return false;
          }
          continue;
        }
        if (
          this.depth === 0 &&
          (ch === ',' || ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r')
        ) {
          // a scalar item ended
          if (!this.emit()) return false;
          continue;
        }
        this.item += ch;
        continue;
      }

      // between items (or before/after the top-level array)
      if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') continue;
      if (!this.started) {
        if (ch !== '[') return this.fail();
        this.started = true;
        continue;
      }
      if (this.done) return this.fail(); // trailing garbage
      if (ch === ',') continue;
      if (ch === ']') {
        this.done = true;
        continue;
      }
      // an item begins
      this.inItem = true;
      this.depth = 0;
      this.item = '';
      this.inString = false;
      this.escaped = false;
      i--; // reprocess this char as item content
    }
    return true;
  }

  /** Flush + final validity (yajl_complete_parse). */
  End(): boolean {
    if (this.failed) return false;
    if (this.inItem && this.depth === 0 && !this.inString && this.item !== '') {
      if (!this.emit()) return false;
    }
    return this.started && this.done;
  }

  private emit(): boolean {
    let obj: unknown;
    try {
      obj = JSON.parse(this.item) as unknown;
    } catch {
      return this.fail();
    }
    this.inItem = false;
    this.item = '';
    this.cb?.OnObject(obj);
    return true;
  }

  private fail(): boolean {
    this.failed = true;
    return false;
  }
}

//
// IndexLoader — mpshared/indexloader.cpp
//

export class IndexLoader implements ArrayItemsCallback {
  // mpshared/indexloader.h:34-35
  protected index_: IndexEntry[] = [];

  // mpshared/indexloader.cpp:8-9 — IndexLoader() {}

  // mpshared/indexloader.cpp:11-38 — InitFromFile (SHAPE NOTE 3: the fopen +
  // 512-byte fread loop collapses; the builder semantics are identical)
  InitFromText(text: string): boolean {
    this.index_ = [];
    const builder = new JsonArrayItemsBuilder();
    builder.Start(this);
    if (!builder.Update(text)) return false;
    builder.End(); // the C++ ignores End's result here
    return true;
  }

  // mpshared/indexloader.cpp:40-42
  GetCount(): number {
    return this.index_.length;
  }

  // mpshared/indexloader.cpp:44-49
  GetEntry(i: number): IndexEntry | null {
    if (i < 0 || i >= this.index_.length) return null;
    return this.index_[i];
  }

  // mpshared/indexloader.cpp:51-158 — one catalog row:
  // [id, hash, title, min players, max players, mission count]
  OnObject(obj: unknown): void {
    const cExpected = 6;

    // Must be array of strings.
    let error = false;
    if (!Array.isArray(obj)) error = true;
    const a = obj as unknown[];
    if (!error) {
      if (a.length !== cExpected) error = true;
    }
    // (the per-element string check is commented out in the C++ — preserved)

    const entry: IndexEntry = {
      packid: { id: 0, hash: new Uint8Array(16) },
      title: '',
      cPlayersMin: 0,
      cPlayersMax: 0,
      cMissions: 0,
      inIndex: false,
    };

    // id
    if (!error) {
      if (typeof a[0] !== 'number') {
        error = true;
      } else {
        // id is a full dword; GetInteger (int) would truncate ids >= 2^31
        entry.packid.id = Math.trunc(a[0]) >>> 0;
      }
    }

    // hash
    if (!error) {
      if (typeof a[1] !== 'string') {
        error = true;
      } else {
        const s = a[1];
        if (s.length !== 32) error = true;
        if (!FromHex(s, entry.packid.hash, 16)) error = true;
      }
    }

    // title
    if (!error) {
      if (typeof a[2] !== 'string') error = true;
      else entry.title = a[2];
    }

    // min players
    if (!error) {
      if (typeof a[3] !== 'number') error = true;
      else entry.cPlayersMin = Math.trunc(a[3]) | 0;
    }

    // max players
    if (!error) {
      if (typeof a[4] !== 'number') error = true;
      else entry.cPlayersMax = Math.trunc(a[4]) | 0;
    }

    // mission count
    if (!error) {
      if (typeof a[5] !== 'number') error = true;
      else entry.cMissions = Math.trunc(a[5]) | 0;
    }

    if (error) {
      this.OnParseError();
      return;
    }

    entry.inIndex = true;
    this.index_.push(entry);
  }

  // mpshared/indexloader.cpp:160-161
  protected OnParseError(): void {}
}

//
// HttpIndexLoader — game/httpindexloader.cpp
//

// game/httpindexloader.h:28-38
export const SORT_UNSORTED = 0;
export const SORT_INSTALLEDASCENDING = 1;
export const SORT_INSTALLEDDESCENDING = 2;
export const SORT_TITLEASCENDING = 3;
export const SORT_TITLEDESCENDING = 4;
export const SORT_PLAYERSASCENDING = 5;
export const SORT_PLAYERSDESCENDING = 6;
export const SORT_MISSIONSASCENDING = 7;
export const SORT_MISSIONSDESCENDING = 8;
export type SortType = number;

// game/httpindexloader.cpp:268-272
export function InstallAscendingSort(e1: IndexEntry, e2: IndexEntry): boolean {
  const s_ppackm = HttpIndexLoader.s_ppackm_!;
  return s_ppackm.IsInstalled(e1.packid) < s_ppackm.IsInstalled(e2.packid);
}

// game/httpindexloader.cpp:274-278
export function InstallDescendingSort(e1: IndexEntry, e2: IndexEntry): boolean {
  const s_ppackm = HttpIndexLoader.s_ppackm_!;
  return s_ppackm.IsInstalled(e1.packid) > s_ppackm.IsInstalled(e2.packid);
}

// game/httpindexloader.cpp:280-282 — stricmp (ASCII case-insensitive)
export function TitleAscendingSort(e1: IndexEntry, e2: IndexEntry): boolean {
  return e1.title.toLowerCase() < e2.title.toLowerCase();
}

// game/httpindexloader.cpp:284-286
export function TitleDescendingSort(e1: IndexEntry, e2: IndexEntry): boolean {
  return e1.title.toLowerCase() > e2.title.toLowerCase();
}

// game/httpindexloader.cpp:288-298
export function PlayersAscendingSort(e1: IndexEntry, e2: IndexEntry): boolean {
  if (e1.cPlayersMin < e2.cPlayersMin) return true;
  if (e1.cPlayersMin === e2.cPlayersMin) {
    if (e1.cPlayersMax < e2.cPlayersMax) return true;
  }
  return false;
}

// game/httpindexloader.cpp:300-310
export function PlayersDescendingSort(e1: IndexEntry, e2: IndexEntry): boolean {
  if (e1.cPlayersMin > e2.cPlayersMin) return true;
  if (e1.cPlayersMin === e2.cPlayersMin) {
    if (e1.cPlayersMax > e2.cPlayersMax) return true;
  }
  return false;
}

// game/httpindexloader.cpp:312-315
export function MissionsAscendingSort(e1: IndexEntry, e2: IndexEntry): boolean {
  return e1.cMissions < e2.cMissions;
}

// game/httpindexloader.cpp:317-320
export function MissionsDescendingSort(e1: IndexEntry, e2: IndexEntry): boolean {
  return e1.cMissions > e2.cMissions;
}

// game/httpindexloader.cpp:322-334
type SortFunction = (e1: IndexEntry, e2: IndexEntry) => boolean;
const s_apfnSort: (SortFunction | null)[] = [
  null,
  InstallAscendingSort,
  InstallDescendingSort,
  TitleAscendingSort,
  TitleDescendingSort,
  PlayersAscendingSort,
  PlayersDescendingSort,
  MissionsAscendingSort,
  MissionsDescendingSort,
];

export class HttpIndexLoader extends IndexLoader implements HttpResponseHandler {
  // game/httpindexloader.cpp:336 — static PackManager *s_ppackm_ (the install
  // sort comparators reach it; the ctor assigns it, exactly like the C++)
  static s_ppackm_: PackManager | null = null;

  private service_: HttpService | null;
  private readonly createMissionList: CreateMissionListFn;
  private ctx_: unknown = null;
  private callback_: ProgressCallback | null = null;
  private req_: HttpRequest | null = null;
  private builder_ = new JsonArrayItemsBuilder();
  private decoder_ = new TextDecoder();
  private code_ = 0;
  private error_ = false;
  private cbTotal_ = 0;
  private cbLength_ = 0;
  private sort_: SortType = SORT_UNSORTED;

  // game/httpindexloader.cpp:9-16
  constructor(
    service: HttpService | null,
    ppackm: PackManager,
    createMissionList: CreateMissionListFn,
  ) {
    super();
    this.service_ = service;
    HttpIndexLoader.s_ppackm_ = ppackm;
    this.createMissionList = createMissionList;
    this.Reset();
  }

  // game/httpindexloader.cpp:18-20 — ~HttpIndexLoader
  Dispose(): void {
    this.Reset();
  }

  // game/httpindexloader.cpp:22-51
  MergeInstalled(ppackm: PackManager): void {
    // Merge in packs that are installed but not in the index. This way,
    // they show up in the mission list, can be deleted, etc.
    // Enumerate installed packs and do a lookup. To make this fast,
    // first create a map.
    const map = new Map<number, IndexEntry>();
    for (const entry of this.index_) {
      map.set(entry.packid.id >>> 0, entry);
    }

    // Enumerate installed packs
    for (const packid of ppackm.EnumPacks()) {
      // If in the index, move on.
      if (map.has(packid.id >>> 0)) continue;

      // Add it to the index. These packs don't have index info nor
      // necessarily PackInfo, so it has to be accurately generated.
      this.AddIndexEntry(packid, false);
    }
  }

  // game/httpindexloader.cpp:53-98
  AddIndexEntry(ppackid: PackId, fDupCheck = true): void {
    if (fDupCheck) {
      for (const entry of this.index_) {
        if (
          entry.packid.id >>> 0 === ppackid.id >>> 0 &&
          entry.packid.hash.every((b, i) => b === (ppackid.hash[i] ?? 0))
        ) {
          return;
        }
      }
    }

    const pml = this.createMissionList(ppackid, kmltAll);
    if (pml === null) return;
    if (pml.GetCount() === 0) return;

    let cPlayersMin = 999;
    let cPlayersMax = -1;
    let md: { szPackName: string; cPlayersMin: number; cPlayersMax: number } | null = null;
    for (let i = pml.GetCount() - 1; i >= 0; i--) {
      md = pml.GetMissionDescription(i);
      if (md === null) return;
      if (md.cPlayersMin < cPlayersMin) cPlayersMin = md.cPlayersMin;
      if (md.cPlayersMax > cPlayersMax) cPlayersMax = md.cPlayersMax;
    }

    const entry: IndexEntry = {
      packid: { id: ppackid.id >>> 0, hash: ppackid.hash.slice() },
      title: md!.szPackName, // the last description read is mission 0's
      cMissions: pml.GetCount(),
      cPlayersMin,
      cPlayersMax,
      inIndex: false,
    };
    this.index_.push(entry);
    this.sort_ = SORT_UNSORTED;
  }

  // game/httpindexloader.cpp:100-119
  OnRemoved(ppackid: PackId): boolean {
    // Find the pack in the list. If it's not part of the original index,
    // remove it.
    for (let i = 0; i < this.index_.length; i++) {
      const it = this.index_[i];
      if (it.packid.id >>> 0 !== ppackid.id >>> 0) continue;
      let fEqual = true;
      for (let j = 0; j < 16; j++) {
        if ((it.packid.hash[j] ?? 0) !== (ppackid.hash[j] ?? 0)) {
          fEqual = false;
          break;
        }
      }
      if (!fEqual) continue;
      if (it.inIndex) break;
      this.index_.splice(i, 1);
      return true;
    }
    return false;
  }

  // game/httpindexloader.cpp:121-132
  AddFakeEntry(
    ppackid: PackId,
    title: string,
    cPlayersMin: number,
    cPlayersMax: number,
    cMissions: number,
  ): void {
    const entry: IndexEntry = {
      packid: { id: ppackid.id >>> 0, hash: ppackid.hash.slice() },
      title,
      cPlayersMin,
      cPlayersMax,
      cMissions,
      inIndex: false,
    };
    this.index_.push(entry);
    this.sort_ = SORT_UNSORTED;
  }

  // game/httpindexloader.cpp:134-157
  Start(ctx: unknown, callback: ProgressCallback | null): boolean {
    if (this.service_ === null) return false;

    // Cancel any ongoing requests
    this.Reset();

    // Start a new request
    this.ctx_ = ctx;
    this.callback_ = callback;
    this.req_ = this.service_.NewRequest(this);
    if (this.req_ === null) return false;
    this.SetServiceUrl(this.req_);

    // Start the json builder. Tell it to call back with array items.
    this.builder_.Start(this);
    this.decoder_ = new TextDecoder();

    // Submit the request. This will asynchronously call back.
    this.service_.SubmitRequest(this.req_);
    return true;
  }

  // game/httpindexloader.cpp:159-162 — "%s?c=%d&v=%d"
  private SetServiceUrl(req: HttpRequest): void {
    req.SetURL(`${kszIndexUrl}?c=${kdwClientID}&v=${knVersionSimulation}`);
  }

  // game/httpindexloader.cpp:164-178
  Reset(): void {
    this.code_ = 0;
    this.error_ = false;
    if (this.req_ !== null) {
      if (this.service_ !== null) {
        this.service_.ReleaseRequest(this.req_);
      }
      this.req_ = null;
    }
    this.builder_.Reset();
    this.index_ = [];
    this.sort_ = SORT_UNSORTED;
    this.cbTotal_ = 0;
    this.cbLength_ = 0;
  }

  // game/httpindexloader.cpp:180-210
  OnReceivedResponse(preq: HttpRequest, code: number, pheaders: HeadersMap | null): void {
    this.code_ = code;
    if (code >= 400) {
      // Http error
      this.error_ = true;
      if (this.callback_ !== null) {
        this.callback_.OnError(this.ctx_, `Server returned error ${code}`);
      }
      return;
    }
    if (code >= 200 && code < 300) {
      // Success! Get Content-Length and call back
      this.cbLength_ = -1;
      const szLength = pheaders?.GetValue('Content-Length') ?? null;
      if (szLength !== null) {
        const n = _ToInteger(szLength, 10);
        if (n !== null) this.cbLength_ = n;
      }
      if (this.callback_ !== null) {
        this.callback_.OnBegin(this.ctx_, this.cbLength_);
      }
      this.cbTotal_ = 0;
      return;
    }
    // Ignore other status codes. If it's a redirect, OnReceivedResponse
    // will get called again.
    return;
  }

  // game/httpindexloader.cpp:212-230
  OnReceivedData(preq: HttpRequest, pbb: Uint8Array): void {
    if (this.error_) return;
    if (this.code_ >= 200 && this.code_ < 300) {
      if (!this.builder_.Update(this.decoder_.decode(pbb, { stream: true }))) {
        if (this.callback_ !== null) {
          this.error_ = true;
          this.callback_.OnError(this.ctx_, 'Error parsing Mission Pack list');
        }
      } else {
        this.cbTotal_ += pbb.length;
        if (this.callback_ !== null) {
          this.callback_.OnProgress(this.ctx_, this.cbTotal_, this.cbLength_);
        }
      }
    }
  }

  // game/httpindexloader.cpp:232-241
  OnFinishedLoading(_preq: HttpRequest): void {
    if (this.error_) return;
    if (this.code_ >= 200 && this.code_ < 300) {
      if (this.callback_ !== null) {
        this.callback_.OnFinished(this.ctx_);
      }
    }
  }

  // game/httpindexloader.cpp:243-251
  OnError(preq: HttpRequest, pszError: string): void {
    if (this.error_) return;
    this.error_ = true;
    if (this.callback_ !== null) {
      this.callback_.OnError(this.ctx_, pszError);
    }
  }

  // game/httpindexloader.cpp:253-259 — drop items after an error
  override OnObject(obj: unknown): void {
    if (this.error_) {
      return; // delete obj
    }
    super.OnObject(obj);
  }

  // game/httpindexloader.cpp:261-266
  protected override OnParseError(): void {
    this.error_ = true;
    if (this.callback_ !== null) {
      this.callback_.OnError(this.ctx_, 'Error parsing Mission Pack list');
    }
  }

  // game/httpindexloader.cpp:337-343 — std::stable_sort (Array.sort is stable;
  // the boolean strict-weak comparator becomes -1/0/1)
  Sort(sort: SortType): void {
    if (sort === this.sort_) return;
    this.sort_ = sort;
    const pfn = s_apfnSort[sort];
    if (pfn === null || pfn === undefined) return; // s_apfnSort[SORT_UNSORTED] is NULL (the C++ never indexes it)
    this.index_.sort((e1, e2) => (pfn(e1, e2) ? -1 : pfn(e2, e1) ? 1 : 0));
  }
}
